import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Download, FileSpreadsheet, Upload } from 'lucide-react';
import { Button } from '../../common/Button';
import { cn } from '../../../utils/cn';
import { inventoryService } from '../../../services/inventoryService';
import { parseRoastedBulkImportFile } from '../../../utils/roastedBulkImportParser';
import type { GreenBeanPickerItem, ImportRow } from './types';

interface Props {
  selectedGBs: GreenBeanPickerItem[];
  onParsed: (rows: ImportRow[]) => void;
  onBack?: () => void;
}

export const UploadStep: React.FC<Props> = ({ selectedGBs, onParsed, onBack }) => {
  const skipped = selectedGBs.length === 0;
  const [downloading, setDownloading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fallbackGBs, setFallbackGBs] = useState<GreenBeanPickerItem[] | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const downloadedRef = useRef(false);

  // On the skip path, the parser still needs a code → id index so the user's
  // template (which may reference any GB in the company) can be resolved.
  // Fetch the full picker list once and merge it into the index.
  useEffect(() => {
    if (!skipped) return;
    let cancelled = false;
    inventoryService
      .listGreenBeansForRBImportPicker()
      .then((data) => {
        if (!cancelled) setFallbackGBs(data);
      })
      .catch((e) => {
        if (!cancelled) setError(`Failed to load green beans: ${String(e)}`);
      });
    return () => {
      cancelled = true;
    };
  }, [skipped]);

  const codeIndex = useMemo(() => {
    const byCode = new Map<string, { id: string; name: string }>();
    const source = skipped ? fallbackGBs ?? [] : selectedGBs;
    source.forEach((g) => byCode.set(g.code, { id: g.id, name: g.name }));
    return { byCode };
  }, [selectedGBs, skipped, fallbackGBs]);

  const ready = skipped ? fallbackGBs !== null : true;

  const downloadTemplate = useCallback(async () => {
    if (skipped) return;
    setDownloading(true);
    setError(null);
    try {
      await inventoryService.downloadRoastedBulkImportTemplate(
        selectedGBs.map((g) => g.id),
      );
    } catch (e) {
      setError(`Failed to download template: ${String(e)}`);
    } finally {
      setDownloading(false);
    }
  }, [selectedGBs, skipped]);

  useEffect(() => {
    if (!downloadedRef.current && !skipped && selectedGBs.length > 0) {
      downloadedRef.current = true;
      void downloadTemplate();
    }
  }, [selectedGBs, skipped, downloadTemplate]);

  const handleFile = async (file: File) => {
    setParsing(true);
    setError(null);
    try {
      const { rows } = await parseRoastedBulkImportFile(file, codeIndex);
      if (rows.length === 0) {
        setError('No data rows found.');
        return;
      }
      onParsed(rows);
    } catch (e) {
      setError(String(e));
    } finally {
      setParsing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-600">
        {skipped ? (
          <>
            Upload your filled XLSX. Each row needs <strong>Source Green Bean</strong>{' '}
            (in <span className="font-mono text-xs">CODE — Name</span> form),{' '}
            <strong>Stock (kg)</strong>, <strong>Standard Cost</strong>, and{' '}
            <strong>Roast Type</strong>.
          </>
        ) : (
          <>
            Template downloaded automatically. Fill in <strong>Stock (kg)</strong>,{' '}
            <strong>Standard Cost</strong>, and <strong>Roast Type</strong> for each
            row, then upload the saved file below. Add more rows for the same green
            bean if you have multiple roast types.
          </>
        )}
      </p>

      {!skipped && (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="secondary"
            leftIcon={<Download className="h-4 w-4" />}
            onClick={() => void downloadTemplate()}
            isLoading={downloading}
            data-test-id="import-rb-upload-redownload"
          >
            Re-download template
          </Button>
          <span className="text-xs text-gray-500">
            {selectedGBs.length} green bean{selectedGBs.length === 1 ? '' : 's'} selected
          </span>
        </div>
      )}

      <label
        className={cn(
          'flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-sm text-gray-600',
          ready ? 'cursor-pointer hover:bg-gray-100' : 'cursor-not-allowed opacity-60',
        )}
        data-test-id="import-rb-upload-dropzone"
      >
        <FileSpreadsheet className="h-8 w-8 text-gray-400" />
        <span>
          <Upload className="inline h-4 w-4 mr-1 align-text-bottom" />
          {ready ? 'Click to choose your filled XLSX' : 'Loading green beans…'}
        </span>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          disabled={!ready}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
          }}
          data-test-id="import-rb-upload-input"
        />
      </label>

      {parsing && <div className="text-sm text-gray-500">Parsing…</div>}
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {onBack && (
        <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100">
          <Button
            variant="ghost"
            onClick={onBack}
            leftIcon={<ArrowLeft className="h-4 w-4" />}
            data-test-id="import-rb-upload-back"
          >
            Back
          </Button>
        </div>
      )}
    </div>
  );
};
